import { ThemeProvider } from '@mui/material';
import '../src/styles/styles.scss';
import NotificationBar from './components/NotificationBar/NotificationBar';
import CssBaseline from '@mui/material/CssBaseline';
import { RtlProvider } from './styles/chachProvider/style-cache-provider';
// Bridge MUI's @mui/system RTL context. Because @mui/material@7 bundles its own
// nested @mui/system@7 while @mui/x-data-grid@7 reads the hoisted @mui/system@6,
// ThemeProvider's RTL context never reaches the DataGrid. Importing from the
// hoisted @mui/system here populates the same context the grid's useRtl() reads,
// fixing inverted/runaway column resizing in RTL.
import MuiRtlProvider from '@mui/system/RtlProvider';
import { useAppTheme } from './styles/theme';
import { useEffect, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { ToastContainer } from 'react-toastify';

// Lazy load HomePage component
const HomePage = React.lazy(() => import('./pages/HomePage/HomePage'));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '16px',
    }}
  >
    <div
      style={{
        width: '50px',
        height: '50px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    ></div>
    <div style={{ color: '#666', fontSize: '16px' }}>Loading...</div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const direction = 'rtl';
const themeMode = 'light';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      cacheTime: 1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

function App() {
  const appTheme = useAppTheme(themeMode, direction);

  const isRtl = direction === 'rtl';

  useEffect(() => {
    document.body.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  }, [isRtl]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <RtlProvider isRtl={isRtl}>
          <ToastContainer/>
          <ThemeProvider theme={appTheme}>
            <MuiRtlProvider value={isRtl}>
              <CssBaseline />
              <Suspense fallback={<LoadingFallback />}>
                <HomePage />
              </Suspense>
            </MuiRtlProvider>
          </ThemeProvider>
        </RtlProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
